import { FhirObject, asObject, isObject, readObjects, readResourceType, readString } from './abdm-fhir-readers';

// Lets the presenter follow references inside one FHIR bundle. Senders identify resources
// by fullUrl ("urn:uuid:..."), by "Type/id", or by both, so every form is indexed.
export class AbdmFhirBundleIndex {
  readonly resources: FhirObject[];
  private readonly byReference = new Map<string, FhirObject>();
  private readonly sectionTitles = new Map<FhirObject, string>();

  constructor(bundle: FhirObject) {
    const entries = readObjects(bundle, 'entry');
    this.resources = entries.map(entry => asObject(entry['resource'])).filter(isObject);
    entries.forEach(entry => this.indexEntry(entry));
    this.indexSections(readObjects(this.find('Composition'), 'section'));
  }

  find(type: string): FhirObject | undefined {
    return this.resources.find(resource => readResourceType(resource) === type);
  }

  ofTypes(types: readonly string[]): FhirObject[] {
    return this.resources.filter(resource => types.includes(readResourceType(resource) || ''));
  }

  resolve(reference: string | undefined): FhirObject | undefined {
    if (!reference) {
      return undefined;
    }
    return this.byReference.get(reference) || this.byReference.get(this.toRelativeReference(reference));
  }

  sectionTitle(resource: FhirObject): string | undefined {
    return this.sectionTitles.get(resource);
  }

  private indexEntry(entry: FhirObject): void {
    const resource = asObject(entry['resource']);
    if (!resource) {
      return;
    }
    const id = readString(resource, 'id');
    const keys = [readString(entry, 'fullUrl'), id && `${readResourceType(resource)}/${id}`, id && `urn:uuid:${id}`];
    keys.filter((key): key is string => !!key).forEach(key => this.byReference.set(key, resource));
  }

  private indexSections(sections: FhirObject[]): void {
    sections.forEach(section => {
      this.indexSectionEntries(section);
      this.indexSections(readObjects(section, 'section'));
    });
  }

  private indexSectionEntries(section: FhirObject): void {
    const title = readString(section, 'title');
    if (!title) {
      return;
    }
    readObjects(section, 'entry')
      .map(entry => this.resolve(readString(entry, 'reference')))
      .filter(isObject)
      .forEach(resource => this.sectionTitles.set(resource, title));
  }

  // "https://hip.example/fhir/Condition/12" and "Condition/12" name the same resource.
  private toRelativeReference(reference: string): string {
    return reference.split('/').slice(-2).join('/');
  }
}
